import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Share2, Heart, MessageCircle, ArrowLeft } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';
import { useHighlightsStore } from '../stores/StoreProvider';
import toast from 'react-hot-toast';

const HighlightDetail = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const highlightsStore = useHighlightsStore();
  const [highlight, setHighlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHighlight = async () => {
      try {
        setLoading(true);
        const highlightData = await highlightsStore.fetchHighlightById(id);
        console.log('Fetched highlight data:', highlightData);
        setHighlight(highlightData);
      } catch (err) {
        setError(err.message);
        toast.error('Failed to load highlight');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchHighlight();
    }
  }, [id, highlightsStore]);

  useEffect(() => {
    if (highlight) {
      console.log('Highlight data updated:', {
        title: highlight.title,
        description: highlight.description,
        thumbnail_url: highlight.thumbnail_url,
        media_url: highlight.media_url
      });
    }
  }, [highlight]);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/highlight/${id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: highlight?.title || 'Hockey Highlight',
          text: highlight?.description || 'Check out this amazing hockey highlight!',
          url: shareUrl,
        });
      } catch (err) {
        console.log('Error sharing:', err);
        // Fallback to clipboard
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading highlight...</p>
        </div>
      </div>
    );
  }

  if (error || !highlight) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Highlight Not Found</h1>
          <p className="text-gray-600 mb-6">This highlight could not be found or is not public.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Meta tags for social sharing */}
      <Helmet>
        <title>{highlight?.title ? `${highlight.title} | HockeyCoach` : 'HockeyCoach - AI Hockey Coach'}</title>
        <meta name="description" content={highlight?.description || 'Check out this amazing hockey highlight!'} />
        
        {/* Open Graph Meta Tags for Facebook */}
        <meta property="og:type" content="video.other" />
        <meta property="og:title" content={highlight?.title ? `${highlight.title} | HockeyCoach` : 'HockeyCoach - AI Hockey Coach'} />
        <meta property="og:description" content={highlight?.description || 'Check out this amazing hockey highlight!'} />
        <meta property="og:url" content={`${window.location.origin}/highlight/${id}`} />
        <meta property="og:site_name" content="HockeyCoach" />
        <meta property="og:image" content={highlight?.thumbnail_url || highlight?.media_url || `${window.location.origin}/og.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={highlight?.title || 'Hockey Highlight'} />
        
        {/* Video specific meta tags */}
        {highlight?.media_type === 'video' && highlight?.media_url && (
          <>
            <meta property="og:video" content={highlight.media_url} />
            <meta property="og:video:type" content="video/mp4" />
            <meta property="og:video:width" content="1920" />
            <meta property="og:video:height" content="1080" />
          </>
        )}
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={highlight?.title ? `${highlight.title} | HockeyCoach` : 'HockeyCoach - AI Hockey Coach'} />
        <meta name="twitter:description" content={highlight?.description || 'Check out this amazing hockey highlight!'} />
        <meta name="twitter:image" content={highlight?.thumbnail_url || highlight?.media_url || `${window.location.origin}/og.png`} />
        
        {/* Additional Meta Tags */}
        <meta name="author" content={highlight?.user ? `${highlight.user.first_name} ${highlight.user.last_name}` : 'HockeyCoach'} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </button>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handleShare}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Share2 className="w-5 h-5 mr-2" />
                Share
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Highlight Media */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="aspect-video bg-black relative">
            {highlight.media_type === 'video' ? (
              <video
                src={highlight.media_url}
                controls
                className="w-full h-full"
                poster={highlight.thumbnail_url}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={highlight.media_url}
                alt={highlight.title || 'Hockey highlight'}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>

        {/* Highlight Info */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {highlight.title || 'Untitled Highlight'}
              </h1>
              {highlight.description && (
                <p className="text-gray-600 mb-4">{highlight.description}</p>
              )}
            </div>
          </div>

          {/* User Info */}
          {highlight.user && (
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                {highlight.user.avatar ? (
                  <img
                    src={highlight.user.avatar}
                    alt={highlight.user.first_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-gray-600 font-medium">
                    {highlight.user.first_name?.[0]}{highlight.user.last_name?.[0]}
                  </span>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {highlight.user.first_name} {highlight.user.last_name}
                </p>
                <p className="text-sm text-gray-500">
                  {new Date(highlight.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-6">
            <button className="flex items-center text-gray-600 hover:text-red-600 transition-colors">
              <Heart className="w-5 h-5 mr-2" />
              <span>Like</span>
            </button>
            <button className="flex items-center text-gray-600 hover:text-blue-600 transition-colors">
              <MessageCircle className="w-5 h-5 mr-2" />
              <span>Comment</span>
            </button>
            <button
              onClick={handleShare}
              className="flex items-center text-gray-600 hover:text-green-600 transition-colors"
            >
              <Share2 className="w-5 h-5 mr-2" />
              <span>Share</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default HighlightDetail;